import { DataVizType, MoBillboardDataType } from '../../types/content/component.types';
import { KpiType, MoBillboardKpis, MoHeatmapKpis, ScKpis } from '../../types/content/kpi.types';

const DEFAULT_SORTING_INFO = {
  columnName: 'units',
  label: 'Absolute',
  direction: 'descending',
  sortKey: 'units',
  sortType: 'Absolute',
  sortIndex: 1,
};

export type ValidationRule = {
  enumType?: Record<string, string>;
  allowedValues?: readonly string[];
  allowEmpty?: boolean;
  allowNull?: boolean;
  allowUndefined?: boolean;
  isArray?: boolean;
  isBoolean?: boolean;
  minLength?: number;
  maxLength?: number;
  customValidator?: (value: any) => boolean; // for validating individual values with custom logic
};

type DefaultValue = {
  default?: string[];
};

export type PreferenceRule = ValidationRule & DefaultValue;

export function getDefaultValue(field: string): any {
  return PREFERENCE_VALIDATION_RULES[field]?.default;
}

export function isValidEnum<T extends Record<string, string>>(enumObj: T, value: unknown): value is T[keyof T] {
  if (typeof value !== 'string') {
    return false;
  }

  return Object.values(enumObj).includes(value);
}

export function getEnumValues<T extends Record<string, string>>(enumObj: T): string[] {
  return Object.values(enumObj);
}

export function getAllowedValues(rule: ValidationRule): string[] | undefined {
  if (rule.enumType) {
    return getEnumValues(rule.enumType);
  }
  if (rule.allowedValues) {
    return [...rule.allowedValues];
  }
  return undefined;
}

const validateFeatureTiers = (value: string): boolean => {
  if (!value || value === '') return true;

  // Valid formats:
  // 1. "name:f2343_id|17847,17848:All"
  // 2. "grouping:name:f18391_id|463413,13163777:combination|463414:electric|Others"
  // 3. "sgSegment:name:f16566_id|410550:no|410552:yes|410551:na|Others"

  // Pattern: optional(grouping|sgSegment):name:identifier|segment1|segment2|...
  // where segments can be "label" or "id:label" or "id1,id2:label"
  return /^(?:(?:grouping|sgSegment):)?name:[^|]+(?:\|[^|]+)+$/.test(value);
};

const validatePriceTiers = (value: string): boolean => {
  if (!value || value === '') return true;

  if (value.includes('|')) {
    const tiers = value.split('|');

    // Checks each tier to match either:
    // 1. A number (e.g., "0.01", "449", "693")
    // 2. A number with label (e.g., "0:low", "100:medium")
    return tiers.every(tier => {
      tier = tier.trim();
      // Empty tier
      if (tier === '') return false;
      // Just a number
      if (/^\d+(?:\.\d+)?$/.test(tier)) return true;
      // Number with label (e.g., "100:medium")
      if (/^\d+(?:\.\d+)?:[^|]*$/.test(tier)) return true;
      return false;
    });
  }

  // Single value: just a number or number with label
  return /^\d+(?:\.\d+)?(?::[^|]*)?$/.test(value);
};

export const PREFERENCE_VALIDATION_RULES: Record<string, PreferenceRule> = {
  sortingInfo: {
    isArray: true,
    allowEmpty: false,
    default: [JSON.stringify(DEFAULT_SORTING_INFO)],
    customValidator: (value: string) => {
      try {
        JSON.parse(value);
        return true;
      } catch {
        return false;
      }
    },
  },
  dataViz: {
    enumType: DataVizType,
    isArray: true,
    allowEmpty: false,
    default: [DataVizType.chart],
  },
  kpi: {
    enumType: KpiType,
    isArray: true,
    allowEmpty: false,
    default: [KpiType.Revenue],
  },
  moBillboardKpi: {
    allowedValues: MoBillboardKpis,
    isArray: true,
    allowEmpty: false,
    default: [KpiType.Revenue],
  },
  moHeatmapKpi: {
    allowedValues: MoHeatmapKpis,
    isArray: true,
    allowEmpty: false,
    default: [KpiType.ChangeRevenue],
  },
  scKpis: {
    allowedValues: ScKpis,
    isArray: true,
    allowEmpty: true,
  },
  lbPeriod: {
    isArray: true,
    allowEmpty: false,
    minLength: 1,
    default: ['year'],
  },
  sgPeriod: {
    isArray: true,
    allowEmpty: false,
    minLength: 1,
    default: ['year'],
  },
  moPeriod: {
    isArray: true,
    allowEmpty: false,
    minLength: 1,
    default: ['year'],
  },
  scPeriod: {
    isArray: true,
    allowEmpty: false,
    minLength: 1,
    default: ['year'],
  },
  tsSummary: {
    isArray: true,
    allowEmpty: false,
    minLength: 1,
    default: ['ytd'],
  },
  tsDuration: {
    isArray: true,
    allowEmpty: false,
    minLength: 1,
    default: ['last12'],
  },
  hideEmptyState: {
    isArray: true,
    isBoolean: true,
    allowEmpty: false,
    default: ['true'],
  },
  constrainWidth: {
    isArray: true,
    isBoolean: true,
    allowEmpty: false,
    default: ['false'],
  },
  moHeatmapFocus: {
    isArray: true,
    allowEmpty: false,
    minLength: 1,
    default: ['market'],
  },
  target: {
    isArray: true,
    allowEmpty: false,
    minLength: 1,
    default: ['market'],
  },
  moBillboardDataType: {
    enumType: MoBillboardDataType,
    isArray: true,
    allowEmpty: false,
    default: [MoBillboardDataType.BrandName],
  },
  lbGroups: {
    isArray: true,
    allowEmpty: true,
  },
  lbColumns: {
    isArray: true,
    allowEmpty: true,
  },
  rowsToDisplay: {
    isArray: true,
    allowEmpty: true,
    allowNull: true,
    customValidator: (value: string | null): boolean => {
      if (!value || value === '' || value === null) return true;
      if (!/^\d+$/.test(value)) return false;
      const num = parseInt(value, 10);
      return num > 0;
    },
  },
  uiEnrichments: {
    isArray: true,
    allowEmpty: true,
    allowUndefined: true,
  },
  selectedProducts: {
    isArray: true,
    allowEmpty: true,
  },
  sharedBrands: {
    isArray: true,
    allowEmpty: true,
  },
  moKeyBrands: {
    isArray: true,
    allowEmpty: true,
  },
  item_label: {
    isArray: true,
    allowNull: true,
    allowEmpty: true,
  },
  customFeatureTiers: {
    isArray: true,
    allowEmpty: true,
    allowUndefined: true,
    customValidator: validateFeatureTiers,
  },
  customPriceTiers: {
    isArray: true,
    allowEmpty: true,
    allowUndefined: true,
    customValidator: validatePriceTiers,
  },
  priceSegmentation: {
    isArray: true,
    allowEmpty: true,
  },
  grouping: {
    isArray: true,
    allowEmpty: false,
    minLength: 1,
  },
  sgSegment: {
    isArray: true,
    allowEmpty: false,
    minLength: 1,
  },
  rowIdField: {
    isArray: true,
    allowEmpty: false,
    minLength: 1,
    default: ['item_id'],
  },
  moFocusBrand: {
    isArray: true,
    allowEmpty: true,
  },
  moHeatmapXGrouping: {
    isArray: true,
    allowEmpty: true,
  },
  moHeatmapYGrouping: {
    isArray: true,
    allowEmpty: true,
  },
  ids: {
    isArray: true,
    allowEmpty: true,
  },
  analyseOption: {
    isArray: true,
    allowEmpty: false,
    minLength: 1,
    default: ['top5'],
  },
  competitorBrandList: {
    isArray: true,
    allowEmpty: true,
  },
  competitorBrandOption: {
    isArray: true,
    allowEmpty: false,
    minLength: 1,
    default: ['top5'],
  },
  analyseIds: {
    isArray: true,
    allowEmpty: true,
  },
  plPeriod: {
    isArray: true,
    allowEmpty: false,
    minLength: 1,
    default: ['year'],
  },
  plColumns: {
    isArray: true,
    allowEmpty: true,
  },
  plFeatures: {
    isArray: true,
    allowEmpty: true,
  },
  plGroups: {
    isArray: true,
    allowEmpty: true,
  },
  hideCommon: {
    isArray: true,
    isBoolean: true,
    allowEmpty: false,
    default: ['false'],
  },
  // Fields for createNewUnique - custom segmentation configuration
  description: {
    isArray: true,
    allowEmpty: true,
  },
  customSegmentation: {
    isArray: true,
    allowEmpty: false,
    minLength: 1,
    customValidator: validateFeatureTiers,
  },
  min: {
    isArray: true,
    allowNull: true,
    allowEmpty: true,
    customValidator: (value: string | null) => {
      if (value === null || value === '' || value === undefined) return true;
      const num = parseFloat(value);
      return !isNaN(num);
    },
  },
  max: {
    isArray: true,
    allowNull: true,
    allowEmpty: true,
    customValidator: (value: string | null) => {
      if (value === null || value === '' || value === undefined) return true;
      const num = parseFloat(value);
      return !isNaN(num);
    },
  },
  cell: {
    isArray: true,
    allowEmpty: false,
    minLength: 1,
  },
  segmentationLabel: {
    isArray: true,
    allowEmpty: false,
    minLength: 1,
  },
  filterType: {
    isArray: true,
    allowEmpty: false,
    allowedValues: ['numericRange', 'multiSelect'] as const,
  },
  // Fields for createNewUnique - custom price configuration
  currency: {
    isArray: true,
    allowEmpty: false,
    customValidator: (value: string) => {
      if (!value || value === '') return false;

      // Accept 3-letter uppercase currency code for local currency (ISO 4217 format)
      return /^[A-Z]{3}$/.test(value);
    },
    default: ['EUR'],
  },
  roundTo: {
    isArray: true,
    allowEmpty: false,
    customValidator: (value: string) => {
      if (!value || value === '') return false;
      const num = parseFloat(value);
      return !isNaN(num) && num > 0;
    },
  },
  priceClasses: {
    isArray: true,
    allowEmpty: false,
    customValidator: (value: string) => {
      if (!value || value === '') return false;
      return validatePriceTiers(value);
    },
  },
  quantityType: {
    isArray: true,
    allowEmpty: false,
    allowedValues: ['units', 'plk'] as const,
  },
};
